import { ReactElement, useState } from "react";
import { Label, Text, TextArea } from "@xometry/xometry_loft";
import { useContextSelector } from "use-context-selector";
import { useMutation } from "@apollo/client";
import {
  AddProposalCommentMutationResult,
  AddProposalCommentMutationVariables,
  AddProposalCommentQuery,
  ProposalCommentInput,
} from "../../../schema/generated/schema";
import Modal from "../Common/Modal";
import { ModalInputContainer, ModalStyles } from "./styles";
import ProposalContext from "../contexts/proposalContext";
import UserContext from "../contexts/userContext";
import { useConfig } from "../contexts/configContext";

interface AddCommentModalProps {
  proposalId: string;
  modalOpen: boolean;
  setModal: (boolean) => void;
  onSubmit?: () => void;
}

const AddCommentModal = ({
  proposalId,
  modalOpen,
  setModal,
  onSubmit,
}: AddCommentModalProps): ReactElement => {
  const [formData, setFormData] = useState({ proposalComment: "" });
  const proposalChatChannel = useContextSelector(
    ProposalContext,
    context => context.data?.chatChannel
  );
  const loggedInUserId = useContextSelector(
    UserContext,
    ctx => ctx.loggedInUser?.id
  );
  const { mockStreamChat } = useConfig();
  const userId = mockStreamChat ? "long-resonance-4" : loggedInUserId;

  const [addProposalComment] = useMutation<
    AddProposalCommentMutationResult,
    AddProposalCommentMutationVariables
  >(AddProposalCommentQuery);

  const handleSave = async () => {
    if (formData.proposalComment !== "") {
      if (proposalChatChannel) {
        await proposalChatChannel.sendMessage({
          text: formData.proposalComment,
        });
      }

      const input: ProposalCommentInput = {
        userId: userId as string,
        comment: formData.proposalComment,
      };

      await addProposalComment({
        variables: {
          proposalId,
          proposalComment: input,
          source: "send request",
        },
      });
    } else {
      console.log("unable to send message");
    }
  };

  const handleSubmit = async () => {
    try {
      await handleSave();
      onSubmit?.();
      setModal(false);
    } catch (err) {
      console.error(err, "failed validation");
    }
  };

  const saveData = (name: string, value: string | number | boolean) => {
    setFormData(currentValue => ({ ...currentValue, [name]: value }));
  };

  return (
    <Modal
      isOpen={modalOpen}
      selector="#modal-mount"
      title={<Text kind="panel-title">Submit Proposal</Text>}
      description={
        <Text>
          Once you submit your request, the supplier will be able to add
          pricing, line items, and may ask for clarification on this proposal.
        </Text>
      }
      submitActionText="Send Request"
      handleSubmit={handleSubmit}
      handleCloseButton={() => {
        setModal(false);
      }}
      handleCancel={() => {
        setModal(false);
      }}
      cssStyle={ModalStyles}
      buttonScale="large"
    >
      <ModalInputContainer>
        <Label className="proposal-comment--label">
          Additional Comments <span>Optional</span>
        </Label>
        <TextArea
          className="proposal-comment--textarea"
          scale="large"
          name="proposalComment"
          onBlur={({ target }) => saveData(target.name, target.value)}
        />
      </ModalInputContainer>
    </Modal>
  );
};

AddCommentModal.defaultProps = {
  onSubmit: undefined,
};

export default AddCommentModal;
